import React, {useCallback, useEffect, useMemo, useRef, useState} from 'react';
import DataGrid, {
  Button as GridButton,
  Column,
  ColumnFixing,
  FilterRow,
  Paging,
  RemoteOperations,
  Scrolling,
  Editing,
  Lookup,
  Toolbar,
  Item
} from 'devextreme-react/data-grid';
import Button from 'devextreme-react/button';
import {getMerchantStore, getPsTypeStore, getPaymentTypeStore, getPaymentSystemStore} from '../../api/lookups';
import {getDataSource} from '../../api/dataSource';
import './merchants-scheme-ps.scss';

const GRID_COLUMNS_LS_KEY = 'grid:merchants-scheme-ps:columns:v1';

const DEFAULT_COLUMN_WIDTHS = {
  MerchantID: 280,
  PaymentSystemTypeID: 280,
  PaymentSystemID: 280,
  PaymentTypeID: 150,
  PaymentSubTypeName: 150,
  IsAllowChangePaymentSubType: 150,
  IsPanAllow: 150,
  IsSavePaymentSystemTokenOnConfirm: 150,
  IsAllowGetExternalBalance: 150,
};

const loadColumnWidths = () => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return DEFAULT_COLUMN_WIDTHS;
    const raw = window.localStorage.getItem(GRID_COLUMNS_LS_KEY);
    if (!raw) return DEFAULT_COLUMN_WIDTHS;

    const parsed = JSON.parse(raw);
    const storedWidths = parsed && typeof parsed === 'object' ? parsed.widths : null;
    if (!storedWidths || typeof storedWidths !== 'object') return DEFAULT_COLUMN_WIDTHS;

    const allowedKeys = new Set(Object.keys(DEFAULT_COLUMN_WIDTHS));
    const sanitized = {};

    Object.keys(storedWidths).forEach((key) => {
      if (!allowedKeys.has(key)) return;
      const width = storedWidths[key];
      if (typeof width !== 'number' || !Number.isFinite(width)) return;
      if (width < 60 || width > 1000) return;
      sanitized[key] = width;
    });

    const merged = {...DEFAULT_COLUMN_WIDTHS, ...sanitized};

    const hadUnknownKeys = Object.keys(storedWidths).some((key) => !allowedKeys.has(key));
    if (hadUnknownKeys) {
      window.localStorage.setItem(GRID_COLUMNS_LS_KEY, JSON.stringify({widths: sanitized}));
    }

    return merged;
  } catch (e) {
    return DEFAULT_COLUMN_WIDTHS;
  }
};

const saveColumnWidths = (widths) => {
  try {
    if (typeof window === 'undefined' || !window.localStorage) return;
    window.localStorage.setItem(GRID_COLUMNS_LS_KEY, JSON.stringify({widths}));
  } catch (e) {
    // ignore
  }
};

const toIntOrNull = (value) => {
  if (value === null || value === undefined || value === '') return null;
  const n = Number(value);
  return Number.isNaN(n) ? null : n;
};

const to01 = (value) => {
  if (value === true || value === 1 || value === '1') return 1;
  if (value === false || value === 0 || value === '0') return 0;
  const n = toIntOrNull(value);
  return n === 1 ? 1 : 0;
};

const toBool = (value) => to01(value) === 1;

const getPaymentSubTypeName = (paymentTypeId, paymentSubTypeId) => {
  const pType = toIntOrNull(paymentTypeId);
  const subType = toIntOrNull(paymentSubTypeId);

  if (pType === null || subType === null) return '';

  if (pType === 1) {
    if (subType === 0) return 'refund';
    if (subType === 1) return 'payout';
  }

  if (pType === 2) {
    if (subType === 0) return '2-step';
    if (subType === 1) return 'auto';
  }

  return String(paymentSubTypeId ?? '');
};

const paymentSubTypeNameLookup = [
  {Name: 'refund'},
  {Name: 'payout'},
  {Name: '2-step'},
  {Name: 'auto'},
];

const MerchantsSchemePS = (props) => {
  const haveEnoughRight = useMemo(() => {
    const {userRights} = props;
    return userRights.includes('11');
  }, []);

  const gridRef = useRef(null);
  const [columnWidths, setColumnWidths] = useState(loadColumnWidths);

  // Застосовуємо фільтр MerchantID переданий зі сторінки Мерчанты
  useEffect(() => {
    const LS_KEY = 'filter:merchants-scheme-ps:merchantId';
    let storedId = null;
    try {
      storedId = window.localStorage.getItem(LS_KEY);
      if (storedId) {
        window.localStorage.removeItem(LS_KEY);
      }
    } catch (e) {
      // ignore
    }
    if (!storedId) return;
    const numId = Number(storedId);
    if (!Number.isFinite(numId)) return;
    // Чекаємо поки DataGrid завантажиться, потім застосовуємо фільтр
    const applyFilter = () => {
      if (gridRef.current) {
        gridRef.current.instance().filter(['MerchantID', '=', numId]);
      }
    };
    // Невелика затримка щоб grid встиг ініціалізуватись
    const timerId = setTimeout(applyFilter, 300);
    return () => clearTimeout(timerId);
  }, []);

  const onGridOptionChanged = useCallback(
    (e) => {
      if (!e?.fullName || typeof e.fullName !== 'string') return;
      if (!e.fullName.endsWith('.width')) return;
      if (!e.fullName.startsWith('columns[')) return;
      if (!e.component) return;

      // Avoid React re-renders while the editing popup is open.
      // Re-rendering causes the popup's internal ScrollView to reset to top.
      if (typeof e.component.hasEditData === 'function' && e.component.hasEditData()) {
        return;
      }

      const nextWidths = {};
      Object.keys(DEFAULT_COLUMN_WIDTHS).forEach((dataField) => {
        const width = e.component.columnOption(dataField, 'width');
        if (typeof width === 'number' && Number.isFinite(width)) {
          nextWidths[dataField] = width;
        }
      });

      if (!Object.keys(nextWidths).length) return;

      setColumnWidths((prev) => {
        let changed = false;
        Object.keys(DEFAULT_COLUMN_WIDTHS).forEach((dataField) => {
          if (prev[dataField] !== nextWidths[dataField]) changed = true;
        });
        if (!changed) return prev;

        const merged = {...prev, ...nextWidths};
        saveColumnWidths(merged);
        return merged;
      });
    },
    [setColumnWidths],
  );

  const dataSource = useMemo(
    () =>
      getDataSource({
        key: 'ID',
        object: 'MerchantPaymentSystem',
        sorts: ['MerchantID', 'PaymentSystemTypeID', 'PaymentSystemID'],
        loadAll: true,
        insertKeys: [
          'MerchantID',
          'PaymentSystemTypeID',
          'PaymentSystemID',
          'PaymentTypeID',
          'PaymentSubTypeID',
          'IsAllowChangePaymentSubType',
          'IsPanAllow',
          'IsSavePaymentSystemTokenOnConfirm',
          'IsAllowGetExternalBalance',
        ],
        modifier: (items = []) =>
          items.map((row) => ({
            ...row,
            MerchantID: toIntOrNull(row.MerchantID),
            PaymentSystemTypeID: toIntOrNull(row.PaymentSystemTypeID),
            PaymentSystemID: toIntOrNull(row.PaymentSystemID),
            PaymentTypeID: toIntOrNull(row.PaymentTypeID),
            PaymentSubTypeID: toIntOrNull(row.PaymentSubTypeID),
            PaymentSubTypeName: getPaymentSubTypeName(row.PaymentTypeID, row.PaymentSubTypeID),
            IsAllowChangePaymentSubType: toBool(row.IsAllowChangePaymentSubType),
            IsPanAllow: toBool(row.IsPanAllow),
            IsSavePaymentSystemTokenOnConfirm: toBool(row.IsSavePaymentSystemTokenOnConfirm),
            IsAllowGetExternalBalance: toBool(row.IsAllowGetExternalBalance),
          })),
        paramsModifier: (paramsData) => ({
          ...paramsData,
          MerchantID: toIntOrNull(paramsData.MerchantID),
          PaymentSystemTypeID: toIntOrNull(paramsData.PaymentSystemTypeID),
          PaymentSystemID: toIntOrNull(paramsData.PaymentSystemID),
          PaymentTypeID: toIntOrNull(paramsData.PaymentTypeID),
          PaymentSubTypeID: toIntOrNull(paramsData.PaymentSubTypeID),
          IsAllowChangePaymentSubType: to01(paramsData.IsAllowChangePaymentSubType),
          IsPanAllow: to01(paramsData.IsPanAllow),
          IsSavePaymentSystemTokenOnConfirm: to01(paramsData.IsSavePaymentSystemTokenOnConfirm),
          IsAllowGetExternalBalance: to01(paramsData.IsAllowGetExternalBalance),
        }),
      }),
    [],
  );

  // Lookups data sources
  const merchantStore = useMemo(() => getMerchantStore(), []);
  const psTypeStore = useMemo(() => getPsTypeStore(), []);
  const paymentTypeStore = useMemo(() => getPaymentTypeStore(), []);

  // For cascading lookup (PaymentSystem depends on PaymentSystemType)
  const psStore = useMemo(() => getPaymentSystemStore(), []);

  const setPaymentSystemTypeValue = (rowData, value) => {
    rowData.PaymentSystemTypeID = toIntOrNull(value);
    rowData.PaymentSystemID = null;
  };

  const getPaymentSubTypeStore = (options) => {
    let subTypes = [];
    const pType = options.data && options.data.PaymentTypeID;
    if (pType === 1) {
      subTypes = [{ID: 0, Name: 'refund'}, {ID: 1, Name: 'payout'}];
    } else if (pType === 2) {
      subTypes = [{ID: 0, Name: '2-step'}, {ID: 1, Name: 'auto'}];
    }
    return subTypes;
  };

  const setPaymentTypeValue = (rowData, value) => {
    rowData.PaymentTypeID = value;
    rowData.PaymentSubTypeID = null;
    rowData.PaymentSubTypeName = '';
  };

  const setPaymentSubTypeValue = (rowData, value) => {
    rowData.PaymentSubTypeID = value;
    rowData.PaymentSubTypeName = getPaymentSubTypeName(rowData.PaymentTypeID, value);
  };

  const onInitNewRow = (e) => {
    // defaults
    e.data.__isNewRow = true;
    e.data.IsAllowChangePaymentSubType = false;
    e.data.IsPanAllow = false;
    e.data.IsSavePaymentSystemTokenOnConfirm = false;
    e.data.IsAllowGetExternalBalance = false;
    e.data.PaymentSubTypeName = '';
  };

  const onEditingStart = (e) => {
    if (e?.data) {
      e.data.__isNewRow = false;
    }
  };

  const onEditorPreparing = (e) => {
    if (e.parentType !== 'dataRow') return;

    if (e.dataField === 'PaymentSystemTypeID') {
      const rowIndex = e?.row?.rowIndex;
      const originalOnValueChanged = e.editorOptions?.onValueChanged;

      e.editorOptions.onValueChanged = (args) => {
        const nextTypeId = toIntOrNull(args?.value);

        // Update current field value
        e.setValue(nextTypeId);

        // Clear dependent field and force editor to reflect the change
        if (rowIndex !== undefined && rowIndex !== null) {
          e.component.cellValue(rowIndex, 'PaymentSystemID', null);
        }

        if (typeof originalOnValueChanged === 'function') {
          originalOnValueChanged(args);
        }
      };
    }

    if (e.dataField === 'PaymentSystemID') {
      const rowIndex = e?.row?.rowIndex;
      const isNewRow = !!e?.row?.isNewRow;

      const buildFilter = (psTypeId) => {
        if (psTypeId === null || psTypeId === undefined || psTypeId === '') return ['ID', '=', -1];

        const filters = [['PaymentSystemTypeID', '=', psTypeId]];
        if (isNewRow) {
          filters.push(['Status', '=', 1]);
        }
        return filters.reduce((acc, item) => (acc ? [acc, 'and', item] : item), null);
      };

      const getCurrentPsTypeId = () => {
        // Prefer live value from the grid/form, fallback to row data.
        if (rowIndex !== undefined && rowIndex !== null) {
          return e.component.cellValue(rowIndex, 'PaymentSystemTypeID');
        }
        return e?.row?.data?.PaymentSystemTypeID;
      };

      const initialPsTypeId = getCurrentPsTypeId();
      const initialFilter = buildFilter(initialPsTypeId);
      e.editorOptions.dataSource = {store: psStore, filter: initialFilter};
      e.editorOptions.disabled = initialFilter?.[0] === 'ID' && initialFilter?.[1] === '=' && initialFilter?.[2] === -1;

      const originalOnOpened = e.editorOptions?.onOpened;
      e.editorOptions.onOpened = (args) => {
        const psTypeId = getCurrentPsTypeId();
        const nextFilter = buildFilter(psTypeId);

        try {
          args?.component?.option('dataSource', {store: psStore, filter: nextFilter});
          args?.component?.getDataSource?.()?.load?.();
          args?.component?.option('disabled', nextFilter?.[0] === 'ID' && nextFilter?.[1] === '=' && nextFilter?.[2] === -1);
        } catch (err) {
          // ignore
        }

        if (typeof originalOnOpened === 'function') {
          originalOnOpened(args);
        }
      };
    }
  };

  const refreshGrid = () => {
    if (gridRef.current) {
      gridRef.current.instance().refresh();
    }
  };

  const resetColumns = () => {
    try {
      if (typeof window !== 'undefined' && window.localStorage) {
        window.localStorage.removeItem(GRID_COLUMNS_LS_KEY);
      }
    } catch (e) {
      // ignore
    }
    setColumnWidths(DEFAULT_COLUMN_WIDTHS);
  };

  return (
    <React.Fragment>
      <h4 className={'content-block grid-title'}>Мерчанты-Схема ПС</h4>
      <div className={'content-block grid-wrapper schema-ps-wrapper'}>
        <DataGrid
          ref={gridRef}
          dataSource={dataSource}
          keyExpr={'ID'}
          repaintChangesOnly={true}
          hoverStateEnabled={true}
          height={'100%'}
          width={'100%'}
          wordWrapEnabled={true}
          allowColumnResizing={true}
          columnResizingMode={'widget'}
          columnAutoWidth={false}
          onOptionChanged={onGridOptionChanged}
          onInitNewRow={onInitNewRow}
          onEditingStart={onEditingStart}
          onEditorPreparing={onEditorPreparing}
        >
          <Toolbar>
            <Item location={'before'}>
              <Button text={'Обновить'} onClick={refreshGrid}/>
            </Item>
            <Item location={'before'}>
              <Button
                text={'Очистить фильтр'}
                onClick={() => {
                  if (gridRef.current) {
                    gridRef.current.instance().clearFilter();
                  }
                }}
              />
            </Item>
            <Item location={'before'}>
              <Button text={'Сбросить ширины'} onClick={resetColumns}/>
            </Item>

            <Item location="after">
              <Button
                visible={haveEnoughRight}
                text="Добавить"
                icon="add"
                onClick={() => {
                  if (gridRef.current) {
                    gridRef.current.instance().addRow();
                  }
                }}
              />
            </Item>
          </Toolbar>

          <RemoteOperations filtering={false} paging={false} sorting={false}/>
          <Paging enabled={false}/>
	          <Scrolling mode={'infinite'} showScrollbar={'always'}/>
          <ColumnFixing enabled={true}/>
          <FilterRow visible={true}/>

          <Editing
            mode="popup"
            allowAdding={haveEnoughRight}
            allowUpdating={haveEnoughRight}
            allowDeleting={haveEnoughRight}
            useIcons={true}
            popup={{
              title: 'Схема ПС',
              showTitle: true,
              width: 700,
              height: 525,
            }}
            form={{
              colCount: 1,
              labelLocation: 'left',
              items: [
                'MerchantID', 'PaymentSystemTypeID', 'PaymentSystemID', 'PaymentTypeID', 'PaymentSubTypeID',
                'IsAllowChangePaymentSubType', 'IsPanAllow', 'IsSavePaymentSystemTokenOnConfirm', 'IsAllowGetExternalBalance',
              ]
            }}
          />

          <Column dataField="MerchantID" caption="Мерчант" setCellValue={(rowData, value) => {
            rowData.MerchantID = value;
          }} width={columnWidths.MerchantID}>
            <Lookup dataSource={merchantStore} valueExpr="ID" displayExpr="Name"/>
          </Column>

          <Column
            dataField="PaymentSystemTypeID"
            caption="ПС (Тип)"
            setCellValue={setPaymentSystemTypeValue}
            width={columnWidths.PaymentSystemTypeID}
          >
            <Lookup dataSource={psTypeStore} valueExpr="ID" displayExpr="Name"/>
          </Column>

          <Column dataField="PaymentSystemID" caption="ПС (Конфигурация)" width={columnWidths.PaymentSystemID}>
            <Lookup dataSource={psStore} valueExpr="ID" displayExpr="Name"/>
          </Column>

          <Column
            dataField="PaymentTypeID"
            caption="Тип платежа"
            setCellValue={setPaymentTypeValue}
            width={columnWidths.PaymentTypeID}
          >
            <Lookup dataSource={paymentTypeStore} valueExpr="ID" displayExpr="Name"/>
          </Column>

          <Column
            dataField="PaymentSubTypeName"
            caption="Тип обработки"
            allowEditing={false}
            width={columnWidths.PaymentSubTypeName}
          >
            <Lookup dataSource={paymentSubTypeNameLookup} valueExpr="Name" displayExpr="Name"/>
          </Column>

          <Column dataField="PaymentSubTypeID" caption="Тип обработки" visible={false} setCellValue={setPaymentSubTypeValue}>
            <Lookup dataSource={getPaymentSubTypeStore} valueExpr="ID" displayExpr="Name"/>
          </Column>

          <Column dataField="IsAllowChangePaymentSubType" caption="Смена типа" dataType="boolean"
                  alignment="left"
                  width={columnWidths.IsAllowChangePaymentSubType}
                  setCellValue={(rowData, value) => {
                    rowData.IsAllowChangePaymentSubType = value;
                  }}/>

          <Column dataField="IsPanAllow" caption="PAN-выплата" dataType="boolean"
                  alignment="left"
                  width={columnWidths.IsPanAllow}
                  setCellValue={(rowData, value) => {
                    rowData.IsPanAllow = value;
                  }}/>

          <Column dataField="IsSavePaymentSystemTokenOnConfirm" caption="Токенизация" dataType="boolean"
                  alignment="left"
                  width={columnWidths.IsSavePaymentSystemTokenOnConfirm}
                  setCellValue={(rowData, value) => {
                    rowData.IsSavePaymentSystemTokenOnConfirm = value;
                  }}/>

          <Column dataField="IsAllowGetExternalBalance" caption="Проверка баланса" dataType="boolean"
                  alignment="left"
                  width={columnWidths.IsAllowGetExternalBalance}
                  setCellValue={(rowData, value) => {
                    rowData.IsAllowGetExternalBalance = value;
                  }}/>

          <Column type="buttons" fixed={true} fixedPosition="right" width={130}>
            <GridButton visible={haveEnoughRight} name="edit"/>
            <GridButton visible={haveEnoughRight} name="delete"/>
          </Column>

        </DataGrid>
      </div>
    </React.Fragment>
  );
};

export default MerchantsSchemePS;
